import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getContractorMaintenanceRecords } from '../../services/contractorsService';
import styles from './MaintenanceTable.module.css';

const MaintenanceTable = ({ contractorId }) => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('id');
  const [sortDirection, setSortDirection] = useState('asc');

  useEffect(() => {
    const loadMaintenanceRecords = async () => {
      if (!contractorId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await getContractorMaintenanceRecords(contractorId);
        
        if (response.success && response.data) {
          setTickets(response.data);
        } else {
          setTickets([]);
        }
      } catch (error) {
        console.error('Error loading maintenance records:', error);
        setTickets([]);
      } finally {
        setLoading(false);
      }
    };

    loadMaintenanceRecords();
  }, [contractorId]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB');
  };

  const getStatusClass = (status) => {
    switch (status.toLowerCase()) {
      case 'open':
        return styles.statusOpen;
      case 'in progress':
        return styles.statusInProgress;
      case 'completed':
        return styles.statusCompleted;
      default:
        return '';
    }
  };

  const getUrgencyClass = (urgency) => {
    switch (urgency.toLowerCase()) {
      case 'high':
        return styles.urgencyHigh;
      case 'medium':
        return styles.urgencyMedium;
      case 'low':
        return styles.urgencyLow;
      default:
        return '';
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredTickets = tickets.filter(ticket =>
    Object.values(ticket).some(value =>
      value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const sortedTickets = [...filteredTickets].sort((a, b) => {
    const aValue = a[sortField];
    const bValue = b[sortField];
    
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const totalPages = Math.ceil(sortedTickets.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedTickets = sortedTickets.slice(startIndex, endIndex);

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
        <p>Loading maintenance tickets...</p>
      </div>
    );
  }

  return (
    <div className={styles.dataTableWrapper}>
      <div className={styles.tableControls}>
        <div className={styles.entriesControl}>
          <span>Show</span>
          <select 
            value={itemsPerPage} 
            onChange={(e) => setItemsPerPage(Number(e.target.value))}
            className={styles.entriesSelect}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <span>entries</span>
        </div>
        <div className={styles.searchControl}>
          <label className={styles.searchLabel}>Search:</label>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
            placeholder="Search tickets..."
          />
        </div>
      </div>

      <table className={styles.table}>
        <thead>
          <tr>
            <th onClick={() => handleSort('id')} className={styles.sortable}>
              ID
              {sortField === 'id' && (
                <span className={styles.sortIcon}>
                  {sortDirection === 'asc' ? '↑' : '↓'}
                </span>
              )}
            </th>
            <th onClick={() => handleSort('status')} className={styles.sortable}>
              Status
              {sortField === 'status' && (
                <span className={styles.sortIcon}>
                  {sortDirection === 'asc' ? '↑' : '↓'}
                </span>
              )}
            </th>
            <th onClick={() => handleSort('urgency')} className={styles.sortable}>
              Urgency
              {sortField === 'urgency' && (
                <span className={styles.sortIcon}>
                  {sortDirection === 'asc' ? '↑' : '↓'}
                </span>
              )}
            </th>
            <th onClick={() => handleSort('property_id')} className={styles.sortable}>
              Property ID
              {sortField === 'property_id' && (
                <span className={styles.sortIcon}>
                  {sortDirection === 'asc' ? '↑' : '↓'}
                </span>
              )}
            </th>
            <th onClick={() => handleSort('property_name')} className={styles.sortable}>
              Property Name
              {sortField === 'property_name' && (
                <span className={styles.sortIcon}>
                  {sortDirection === 'asc' ? '↑' : '↓'}
                </span>
              )}
            </th>
            <th onClick={() => handleSort('postcode')} className={styles.sortable}>
              Postcode
              {sortField === 'postcode' && (
                <span className={styles.sortIcon}>
                  {sortDirection === 'asc' ? '↑' : '↓'}
                </span>
              )}
            </th>
            <th onClick={() => handleSort('submitted')} className={styles.sortable}>
              Submitted
              {sortField === 'submitted' && (
                <span className={styles.sortIcon}>
                  {sortDirection === 'asc' ? '↑' : '↓'}
                </span>
              )}
            </th>
            <th onClick={() => handleSort('deadline')} className={styles.sortable}>
              Deadline
              {sortField === 'deadline' && (
                <span className={styles.sortIcon}>
                  {sortDirection === 'asc' ? '↑' : '↓'}
                </span>
              )}
            </th>
            <th onClick={() => handleSort('completed')} className={styles.sortable}>
              Completed
              {sortField === 'completed' && (
                <span className={styles.sortIcon}>
                  {sortDirection === 'asc' ? '↑' : '↓'}
                </span>
              )}
            </th>
            <th onClick={() => handleSort('job_type')} className={styles.sortable}>
              Job type
              {sortField === 'job_type' && (
                <span className={styles.sortIcon}>
                  {sortDirection === 'asc' ? '↑' : '↓'}
                </span>
              )}
            </th>
            <th onClick={() => handleSort('contractor_first')} className={styles.sortable}>
              C. First
              {sortField === 'contractor_first' && (
                <span className={styles.sortIcon}>
                  {sortDirection === 'asc' ? '↑' : '↓'}
                </span>
              )}
            </th>
            <th onClick={() => handleSort('contractor_last')} className={styles.sortable}>
              C. Last
              {sortField === 'contractor_last' && (
                <span className={styles.sortIcon}>
                  {sortDirection === 'asc' ? '↑' : '↓'}
                </span>
              )}
            </th>
            <th onClick={() => handleSort('contractor')} className={styles.sortable}>
              Contractor
              {sortField === 'contractor' && (
                <span className={styles.sortIcon}>
                  {sortDirection === 'asc' ? '↑' : '↓'}
                </span>
              )}
            </th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {paginatedTickets.map((ticket) => (
            <tr key={ticket.id}>
              <td>{ticket.id}</td>
              <td>
                <span className={`${styles.status} ${getStatusClass(ticket.status)}`}>
                  {ticket.status}
                </span>
              </td>
              <td>
                <span className={`${styles.urgency} ${getUrgencyClass(ticket.urgency)}`}>
                  {ticket.urgency}
                </span>
              </td>
              <td>{ticket.property_id}</td>
              <td>{ticket.property_name}</td>
              <td>{ticket.postcode}</td>
              <td>{formatDate(ticket.submitted)}</td>
              <td>{formatDate(ticket.deadline)}</td>
              <td>{formatDate(ticket.completed)}</td>
              <td>{ticket.job_type}</td>
              <td>{ticket.contractor_first}</td>
              <td>{ticket.contractor_last}</td>
              <td>{ticket.contractor}</td>
              <td>
                <div className={styles.actionButtons}>
                  <Link to={`/tickets/${ticket.id}`} className={styles.btnShow}>
                    View
                  </Link>
                  <Link to={`/tickets/${ticket.id}/edit`} className={styles.btnEdit}>
                    Edit
                  </Link>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {sortedTickets.length === 0 && (
        <div className={styles.noData}>
          <p>No maintenance tickets found</p>
        </div>
      )}

      {sortedTickets.length > 0 && (
        <div className={styles.paginationContainer}>
          <div className={styles.paginationInfo}>
            Showing {startIndex + 1} to {Math.min(endIndex, sortedTickets.length)} of {sortedTickets.length} entries
          </div>
          <div className={styles.pagination}>
            <button
              className={`${styles.paginationButton} ${currentPage === 1 ? styles.disabled : ''}`}
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                className={`${styles.paginationButton} ${currentPage === page ? styles.active : ''}`}
                onClick={() => setCurrentPage(page)}
              >
                {page}
              </button>
            ))}
            <button
              className={`${styles.paginationButton} ${currentPage === totalPages ? styles.disabled : ''}`}
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaintenanceTable; 